// Persistent per-user profile keyed to the Firebase anonymous auth UID.
//
// This is the one piece of Chipless that intentionally persists across visits:
// it stores a last-used display name on the user profile (Host does not prefill
// from it) and counts how many games / hands they've played. It is keyed to the
// anonymous auth UID (see auth.js), so it survives refreshes and revisits as
// long as the browser keeps the anonymous session. Clearing browser storage
// resets to a brand-new anonymous identity and discards the profile.
//
// Forward-compatible with real accounts: linkWithCredential() upgrades the
// anonymous account without changing the UID, so this profile carries over.
import { database } from '../firebase/firebaseConfig';
import { ref, get, update, runTransaction, push, set } from 'firebase/database';
import { ensureAuth } from './auth';

const PROFILE_NAME_CACHE_KEY = 'chipless:profileName';

const profileRef = (uid) => ref(database, `users/${uid}`);

/**
 * Ensure a profile node exists for the current anonymous user and refresh
 * lastSeen. Best-effort: never throws into the UI.
 * @returns {Promise<Object|null>} The profile, or null if unavailable.
 */
export async function ensureUserProfile() {
  try {
    const uid = await ensureAuth();
    const now = Date.now();

    const result = await runTransaction(profileRef(uid), (current) => {
      if (!current) {
        return { createdAt: now, lastSeen: now, gamesPlayed: 0, handsPlayed: 0 };
      }
      const updated = { ...current, lastSeen: now };
      if (!updated.createdAt) {
        updated.createdAt = now;
      }
      return updated;
    });

    const profile = result.committed ? result.snapshot.val() : null;
    cacheName(profile?.name);
    return profile;
  } catch (error) {
    console.warn('👤 ensureUserProfile failed:', error);
    return null;
  }
}

/**
 * Read the current user's profile.
 * @returns {Promise<Object|null>}
 */
export async function getUserProfile() {
  try {
    const uid = await ensureAuth();
    const snapshot = await get(profileRef(uid));
    const profile = snapshot.exists() ? snapshot.val() : null;
    cacheName(profile?.name);
    return profile;
  } catch (error) {
    console.warn('👤 getUserProfile failed:', error);
    return null;
  }
}

/**
 * Last-used display name, read synchronously from localStorage.
 * Host does not prefill from this — kept for the user profile record.
 * @returns {string} Cached name, or '' if none.
 */
export function getCachedProfileName() {
  try {
    return localStorage.getItem(PROFILE_NAME_CACHE_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * Persist the player's chosen display name onto their profile.
 * @param {string} name
 */
export async function updateProfileName(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return;
  cacheName(trimmed);
  try {
    const uid = await ensureAuth();
    await update(profileRef(uid), { name: trimmed, lastSeen: Date.now() });
  } catch (error) {
    console.warn('👤 updateProfileName failed:', error);
  }
}

/**
 * Record that the user entered a game room (host, join, or rejoin).
 * Appends a timestamped entry under `gameEntries` for analytics.
 * @param {string} [roomCode]
 */
export async function recordGameEntry(roomCode) {
  try {
    const uid = await ensureAuth();
    const now = Date.now();
    const entryRef = push(ref(database, `users/${uid}/gameEntries`));
    await set(entryRef, {
      enteredAt: now,
      roomCode: (roomCode || '').trim().toUpperCase() || null,
    });
    await update(profileRef(uid), { lastSeen: now });
  } catch (error) {
    console.warn('👤 recordGameEntry failed:', error);
  }
}

/**
 * Increment the user's lifetime games-played counter.
 */
export async function incrementGamesPlayed() {
  await bumpCounter('gamesPlayed');
}

/**
 * Increment the user's lifetime hands-played counter.
 */
export async function incrementHandsPlayed() {
  await bumpCounter('handsPlayed');
}

async function bumpCounter(field) {
  try {
    const uid = await ensureAuth();
    const counterRef = ref(database, `users/${uid}/${field}`);
    await runTransaction(counterRef, (current) => (current || 0) + 1);
  } catch (error) {
    console.warn(`👤 increment ${field} failed:`, error);
  }
}

function cacheName(name) {
  if (!name) return;
  try {
    localStorage.setItem(PROFILE_NAME_CACHE_KEY, name);
  } catch {
    // best-effort
  }
}
